<template>
  <div ref="root" class="relative">
    <h2 class="heading-3">
      {{
        i18n(translations.SeoSellerContentTitle, {
          model: technicalSpecifications.title,
        })
      }}
    </h2>
    <slot />
    <div :class="['text-static-default-low']">
      <SpecificationsOverview
        class="my-12"
        :specifications="technicalSpecificationsArray"
      />
      <!-- The product description could be sent by the API as a
          string containing HTML or JSON. -->
      <!-- eslint-disable vue/no-v-html -->
      <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
      <div
        v-if="!descriptionAsJson.content?.length"
        class="body-1 my-12"
        data-selector="pp-text"
        v-html="description"
      />
      <ContentRenderer
        v-else
        class="body-1 mb-24 mt-12 space-y-12"
        :content="descriptionAsJson.content"
        data-selector="pp-text"
      />

      <SpecificationsTable
        class="my-12"
        :specifications="technicalSpecificationsArray"
        :title="technicalSpecifications.title"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from 'vue'

import type { GetTechnicalSpecificationsResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/technical-specifications'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { isJsonString } from '@backmarket/utils/string/isJsonString'
import { useIntersectionObserver } from '@vueuse/core'

import translations from './SeoContent.translations'
import ContentRenderer from './components/ContentRenderer/ContentRenderer.vue'
import SpecificationsOverview from './components/SpecificationsOverview/SpecificationsOverview.vue'
import SpecificationsTable from './components/SpecificationsTable/SpecificationsTable.vue'

const props = defineProps<{
  description: string
  model: string | undefined
  technicalSpecifications: GetTechnicalSpecificationsResponse
}>()

const i18n = useI18n()
const hasTriggeredImpression = ref(false)
const root = ref<HTMLElement | null>()

const { trackProductBlockImpression } = useTracking()

const technicalSpecificationsArray = computed(() => {
  const { specifications = [] } = props.technicalSpecifications

  return specifications.filter(({ values }) => !isEmpty(values))
})

const descriptionAsJson = computed(() => {
  if (isJsonString(props.description)) {
    return JSON.parse(props.description)
  }

  return { content: [] }
})

const { stop } = useIntersectionObserver(root, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    trackProductBlockImpression({
      block_name: 'pp_tech_specs',
      product_model: props.model || '',
    })
    hasTriggeredImpression.value = true
  }
})

watch(hasTriggeredImpression, () => {
  if (hasTriggeredImpression.value) {
    stop()
  }
})

onUnmounted(() => {
  stop()
})
</script>
